// This is the device detail page
// It shows all drivers installed on a specific device
// Users get here by clicking on a device in the dashboard

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";
import DeviceStatusIcon from "../components/DeviceStatusIcon";
import useSuspended from "../hooks/useSuspended";
import ScheduleUpdateModal from "../components/ScheduleUpdateModal";
import DeployPackageModal from "../components/DeployPackageModal";

// The address of our backend server
const API_URL = config.API_URL;

export default function DeviceDetail() {
  const isSuspended = useSuspended();
  // useParams reads the device ID from the URL
  // For example: http://localhost:3000/devices/abc-123 -> deviceId = "abc-123"
  const { deviceId } = useParams();

  // useNavigate lets us go back to the dashboard
  const navigate = useNavigate();
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");

  // State variables
  const [drivers, setDrivers] = useState([]);     // list of drivers on this device
  const [device, setDevice] = useState(null);     // this is for the computer name
  const [loading, setLoading] = useState(true);   // whether we are still fetching
  const [error, setError] = useState(null);       // any error that occurred
  const [isLegacy, setIsLegacy] = useState(false); // we are adding Legacy option 
  const [isReference, setIsReference] = useState(false); //we are adding reference devices
  const [sortField, setSortField] = useState(null); //we added a sorting filed
  const [sortDirection, setSortDirection] = useState("asc"); //this changes the direction of the sorfting
  const [showUpdateModal, setShowUpdateModal] = useState(false); //shows the modal state
  const [showDeployModal, setShowDeployModal] = useState(false); //shows the deploy package modal
  const [twPackages, setTwPackages] = useState([]); //list of available TriggerWatch packages for this org
  const [showActionsMenu, setShowActionsMenu] = useState(false); //shows the actions dropdown
  const tableRef = useRef(null);
  const [colWidths, setColWidths] = useState(null);

  useEffect(() => {
    if (tableRef.current) {
      const total = tableRef.current.offsetWidth;
      setColWidths([total * 0.25, total * 0.18, total * 0.20, total * 0.13, total * 0.13, total * 0.18]);
    }
  }, []);

  function startResize(e, index) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = colWidths ? colWidths[index] : 0;
    function onMouseMove(e) {
      const newWidth = Math.max(60, startWidth + e.clientX - startX);
      setColWidths(prev => {
        const updated = [...prev];
        updated[index] = newWidth;
        return updated;
      });
    }
    function onMouseUp() {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  useEffect(() => {
    // Fetch all drivers for this device when the page loads
    axios
      .get(`${API_URL}/devices/${deviceId}/drivers`)
      .then((res) => {
        setDrivers(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load driver data");
        setLoading(false);
      });

    // Fetch device info to get hostname for CSV filename
    axios
      .get(`${API_URL}/organizations/${storedUser.organization_id}/devices`)
      .then((res) => {
        const found = res.data.find(d => d.id === deviceId);
        if (found) {
          setDevice(found);
          setIsLegacy(found.is_legacy === "true");
        }

        // Check if this device is a reference device
        axios
          .get(`${API_URL}/organizations/${storedUser.organization_id}/reference-devices`)
          .then((refRes) => {
            const isRef = refRes.data.some(r => r.device_id === deviceId);
            setIsReference(isRef);
          });
      });
  }, [deviceId]);
  
  useEffect(() => {
    // Fetch available TriggerWatch packages for the Deploy Package modal
    axios
      .get(`${API_URL}/organizations/${storedUser.organization_id}/tw-packages`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      })
      .then((res) => setTwPackages(res.data))
      .catch(() => setTwPackages([]));
  }, []);

  function formatDriverDate(dateStr) {
    // Converts Windows driver date formats to readable date
    if (!dateStr || dateStr === "unknown") return "Unknown";
    try {
      let date;
      if (dateStr.length > 8 && dateStr.includes("+")) {
        date = new Date(
          dateStr.slice(0, 4),
          parseInt(dateStr.slice(4, 6)) - 1,
          dateStr.slice(6, 8)
        );
      } else if (dateStr.includes("-")) {
        date = new Date(dateStr.slice(0, 10));
      } else if (dateStr.length >= 8) {
        date = new Date(
          dateStr.slice(0, 4),
          parseInt(dateStr.slice(4, 6)) - 1,
          dateStr.slice(6, 8)
        );
      } else {
        return dateStr;
      }
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  function getSortedDrivers() {
    if (!sortField) return drivers;
    return [...drivers].sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";

      if (sortField === "driver_date") {
        // Parse date for comparison
        const parseDate = (str) => {
          if (!str || str === "unknown") return 0;
          const d = str.length >= 8 ? str.slice(0, 8) : str.replace(/-/g, "").slice(0, 8);
          return parseInt(d) || 0;
        };
        aVal = parseDate(aVal);
        bVal = parseDate(bVal);
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (sortField === "scanned_at") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      // String comparison for device_name, manufacturer, status
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }

  async function handleReferenceToggle() {
    try {
      if (isReference) {
        await axios.delete(`${API_URL}/devices/${deviceId}/remove-reference`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
        });
        setIsReference(false);
      } else {
        await axios.put(`${API_URL}/devices/${deviceId}/set-reference`, null, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
        });
        setIsReference(true);
      }
    } catch (err) {
      alert("Failed to update reference device status");
    }
  }

  async function handleLegacyToggle() {
    const newValue = !isLegacy;
    try {
      await axios.put(`${API_URL}/devices/${deviceId}/legacy?is_legacy=${newValue}`, null, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      });
      setIsLegacy(newValue);
    } catch (err) {
      alert("Failed to update legacy status");
    }
  }

  // Show loading message while data is being fetched
  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading drivers...</p>
    </div>
  );

  // Show error message if something went wrong
  if (error) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-red-400 text-xl">{error}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      <Navbar />

      {/* Main content */}
      <main className="px-8 py-8">

        {/* Page title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                {device && <DeviceStatusIcon online={device.is_online} className="w-5 h-5" />}
                {device ? device.hostname : "Device Drivers"}
              </h1>
              {isLegacy && (
                <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-full">
                  Legacy Device
                </span>
              )}
              {isReference && (
                <span className="bg-blue-900 text-blue-300 text-xs px-2 py-1 rounded-full">
                  Reference Device
                </span>
              )}
            </div>
            <p className="text-gray-400 mt-1">
              {device ? `${device.manufacturer} ${device.model}` : ""} · {drivers.length} drivers found
            </p>
          </div>
          <div className="flex items-center gap-3">

            {/* Actions dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                disabled={isSuspended}
                className={`text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  isSuspended ? "bg-gray-800 text-gray-500 cursor-not-allowed" : "bg-gray-700 hover:bg-gray-600 text-white"
                }`}
              >
                Actions
                <span className="text-xs">{showActionsMenu ? "▲" : "▼"}</span>
              </button>
              {showActionsMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20">
                  <button
                    onClick={async () => {
                      setShowActionsMenu(false);
                      try {
                        await axios.post(`${API_URL}/devices/${deviceId}/checkin-now`, null, {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                        });
                        alert("Check-in requested. The agent will poll for commands within 3 minutes.");
                      } catch (err) {
                        alert("Failed to request check-in.");
                      }
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-700 rounded-t-lg transition-colors"
                  >
                    Check In Now
                  </button>
                  <button
                    onClick={async () => {
                      setShowActionsMenu(false);
                      if (!window.confirm(`Queue an agent update for ${device?.hostname}? The agent will download and reinstall itself on next check-in.`)) return;
                      try {
                        await axios.post(`${API_URL}/devices/${deviceId}/update-agent`, null, {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                        });
                        alert("Agent update queued. The agent will update itself on next check-in.");
                      } catch (err) {
                        alert(err.response?.data?.detail || "Failed to queue agent update.");
                      }
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
                  >
                    Update Agent
                  </button>
                  {device && ["hp", "dell", "dell inc.", "lenovo"].includes(device.manufacturer?.toLowerCase()) && (
                    <button
                      onClick={() => {
                        setShowActionsMenu(false);
                        setShowUpdateModal(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-blue-300 hover:bg-gray-700 transition-colors"
                    >
                      Update Drivers
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowActionsMenu(false);
                      setShowDeployModal(true);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-blue-300 hover:bg-gray-700 transition-colors"
                  >
                    Deploy Package
                  </button>
                  <div className="border-t border-gray-700 my-1" />
                  <button
                    onClick={() => {
                      setShowActionsMenu(false);
                      handleReferenceToggle();
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
                  >
                    {isReference ? "Remove Reference Device" : "Set as Reference Device"}
                  </button>
                  <button
                    onClick={() => {
                      setShowActionsMenu(false);
                      handleLegacyToggle();
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-700 rounded-b-lg transition-colors"
                  >
                    {isLegacy ? "Remove Legacy Status" : "Mark as Legacy"}
                  </button>
                </div>
              )}
            </div>

            {/* Delete Device button */}
            <button
              onClick={async () => {
                const warningMsg = isReference
                  ? `${device?.hostname} is currently set as a Reference Device. Deleting it will remove the compliance baseline for this model.\n\nAre you sure you want to delete this device and all its driver records?`
                  : `Delete ${device?.hostname}? This will also delete all driver records for this device.`;
                if (!window.confirm(warningMsg)) return;
                try {
                  await axios.delete(`${API_URL}/devices/${deviceId}`, {
                    headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                  });
                  navigate("/");
                } catch (err) {
                  alert("Failed to delete device.");
                }
              }}
              disabled={isSuspended}
              className={`text-sm px-4 py-2 rounded-lg transition-colors ${
                isSuspended ? "bg-gray-800 text-gray-500 cursor-not-allowed" : "bg-red-800 hover:bg-red-700 text-white"
              }`}
            >
              Delete Device
            </button>

            {/* Export CSV button */}
            <button
              onClick={() => {
                const headers = ["Device Name", "Manufacturer", "Driver Version", "Driver Date", "Status", "Scanned At"];
                const rows = drivers.map(d => [
                  d.device_name,
                  d.manufacturer,
                  d.driver_version,
                  d.driver_date,
                  d.status,
                  new Date(d.scanned_at).toLocaleString()
                ]);
                const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                const now = new Date();
                const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}-${String(now.getMinutes()).padStart(2,'0')}`;
                a.download = `Drivers_${device ? device.hostname : deviceId}_${timestamp}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Driver status summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">

          {/* Total drivers card */}
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <p className="text-gray-400 text-sm mb-1">Total Drivers</p>
            <p className="text-3xl font-bold text-white">{drivers.length}</p>
            <p className="text-gray-500 text-xs mt-2">Installed on this device</p>
          </div>

          {/* Up to date drivers card */}
          <div className="bg-gray-900 rounded-xl p-6 border border-green-900">
            <p className="text-gray-400 text-sm mb-1">Up to Date</p>
            <p className="text-3xl font-bold text-green-400">
              {drivers.filter(d => d.status === "up_to_date").length}
            </p>
            <p className="text-gray-500 text-xs mt-2">Recently updated</p>
          </div>

          {/* Aging drivers card */}
          <div className="bg-gray-900 rounded-xl p-6 border border-yellow-900">
            <p className="text-gray-400 text-sm mb-1">Aging</p>
            <p className="text-3xl font-bold text-yellow-400">
              {drivers.filter(d => d.status === "aging").length}
            </p>
            <p className="text-gray-500 text-xs mt-2">1-2 years old</p>
          </div>

          {/* Stable drivers card */}
          <div className="bg-gray-900 rounded-xl p-6 border border-blue-900">
            <p className="text-gray-400 text-sm mb-1">Stable</p>
            <p className="text-3xl font-bold text-blue-400">
              {drivers.filter(d => d.status === "stable").length}
            </p>
            <p className="text-gray-500 text-xs mt-2">2+ years, matches reference</p>
          </div>

          {/* Outdated drivers card */}
          <div className="bg-gray-900 rounded-xl p-6 border border-red-900">
            <p className="text-gray-400 text-sm mb-1">Outdated</p>
            <p className="text-3xl font-bold text-red-400">
              {drivers.filter(d => d.status === "outdated").length}
            </p>
            <p className="text-gray-500 text-xs mt-2">Behind reference version</p>
          </div>

        </div>

        {/* Drivers table */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <h2 className="text-white font-semibold mb-4">Installed Drivers</h2>

          {drivers.length === 0 ? (
            <p className="text-gray-400">No drivers found for this device</p>
          ) : (
            <div className="overflow-x-auto" ref={tableRef}>
            <table className="text-sm" style={{ tableLayout: "fixed", width: colWidths ? colWidths.reduce((a, b) => a + b, 0) : "100%" }}>
              <colgroup>
                {colWidths && colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
              </colgroup>
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  {[
                    { label: "Device Name", field: "device_name" },
                    { label: "Manufacturer", field: "manufacturer" },
                    { label: "Version", field: "driver_version" },
                    { label: "Driver Date", field: "driver_date" },
                    { label: "Status", field: "status" },
                    { label: "Scanned At", field: "scanned_at" },
                  ].map(({ label, field }, i) => (
                    <th
                      key={field}
                      className="text-left py-3 cursor-pointer hover:text-white transition-colors select-none relative"
                      style={{ width: colWidths ? colWidths[i] : "auto" }}
                      onClick={() => handleSort(field)}
                    >
                      {label} {sortField === field ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                      <div
                        onMouseDown={(e) => { e.stopPropagation(); startResize(e, i); }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-0 top-0 h-full w-2 cursor-col-resize flex items-center justify-center group"
                      >
                        <div className="w-px h-4 bg-gray-600 group-hover:bg-blue-400 transition-colors" />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getSortedDrivers().map((driver) => (
                  <tr
                    key={driver.id}
                    className="border-b border-gray-800 hover:bg-gray-800 transition-colors"
                  >
                    <td className="py-3 text-white font-medium">{driver.device_name}</td>
                    <td className="py-3 text-gray-400">{driver.manufacturer}</td>
                    <td className="py-3 text-gray-400 font-mono">{driver.driver_version}</td>
                    <td className="py-3 text-gray-400">{formatDriverDate(driver.driver_date)}</td>
                    <td className="py-3">
                      {driver.status === "up_to_date" && (
                        <div className="flex items-center gap-1">
                          <span className="bg-green-900 text-green-300 text-xs px-2 py-1 rounded-full">Up to Date</span>
                          <span className="relative group cursor-pointer">
                            <span className="text-gray-500 text-xs">ℹ️</span>
                            <span className="absolute bottom-6 left-0 w-48 bg-gray-800 text-gray-200 text-xs rounded-lg p-2 hidden group-hover:block z-10 border border-gray-700">
                              Driver was updated within the last 365 days.
                            </span>
                          </span>
                        </div>
                      )}
                      {driver.status === "aging" && (
                        <div className="flex items-center gap-1">
                          <span className="bg-yellow-900 text-yellow-300 text-xs px-2 py-1 rounded-full">Aging</span>
                          <span className="relative group cursor-pointer">
                            <span className="text-gray-500 text-xs">ℹ️</span>
                            <span className="absolute bottom-6 left-0 w-56 bg-gray-800 text-gray-200 text-xs rounded-lg p-2 hidden group-hover:block z-10 border border-gray-700">
                              Driver is 1-2 years old but matches the reference device baseline. Monitor for updates.
                            </span>
                          </span>
                        </div>
                      )}
                      {driver.status === "stable" && (
                        <div className="flex items-center gap-1">
                          <span className="bg-blue-900 text-blue-300 text-xs px-2 py-1 rounded-full">Stable</span>
                          <span className="relative group cursor-pointer">
                            <span className="text-gray-500 text-xs">ℹ️</span>
                            <span className="absolute bottom-6 left-0 w-56 bg-gray-800 text-gray-200 text-xs rounded-lg p-2 hidden group-hover:block z-10 border border-gray-700">
                              Driver has not been updated in over 2 years but matches the reference device baseline. The manufacturer has likely stopped releasing updates.
                            </span>
                          </span>
                        </div>
                      )}
                      {driver.status === "outdated" && (
                        <div className="flex items-center gap-1">
                          <span className="bg-red-900 text-red-300 text-xs px-2 py-1 rounded-full">Outdated</span>
                          <span className="relative group cursor-pointer">
                            <span className="text-gray-500 text-xs">ℹ️</span>
                            <span className="absolute bottom-6 left-0 w-48 bg-gray-800 text-gray-200 text-xs rounded-lg p-2 hidden group-hover:block z-10 border border-gray-700">
                              Driver is older than the reference device version. An update may be available.
                            </span>
                          </span>
                        </div>
                      )}
                      {driver.status === "unknown" && (
                        <div className="flex items-center gap-1">
                          <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-full">Unknown</span>
                          <span className="relative group cursor-pointer">
                            <span className="text-gray-500 text-xs">ℹ️</span>
                            <span className="absolute bottom-6 left-0 w-48 bg-gray-800 text-gray-200 text-xs rounded-lg p-2 hidden group-hover:block z-10 border border-gray-700">
                              Driver date could not be determined.
                            </span>
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 text-gray-400">
                      {new Date(driver.scanned_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
            
      {/* Deploy Package Modal */}
        {showDeployModal && (
          <DeployPackageModal
            title={`Deploy Package — ${device?.hostname}`}
            subtitle={`${device?.manufacturer} ${device?.model} · Select a package to deploy to this device.`}
            packages={twPackages}
            onConfirm={async (packageId, scheduledAt) => {
              try {
                const params = new URLSearchParams({
                  package_id: packageId,
                  target_type: "single_device",
                  target_device_id: deviceId
                });
                if (scheduledAt) params.append("scheduled_at", scheduledAt);

                await axios.post(
                  `${API_URL}/organizations/${storedUser.organization_id}/tw-deployments?${params.toString()}`,
                  {},
                  { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
                );
                setShowDeployModal(false);
                alert(scheduledAt
                  ? `✅ Deployment scheduled for ${new Date(scheduledAt).toLocaleString()}`
                  : "✅ Deployment queued. The agent will pick it up within 60 seconds."
                );
              } catch (err) {
                alert(err.response?.data?.detail || "❌ Failed to create deployment. Please try again.");
              }
            }}
            onCancel={() => setShowDeployModal(false)}
          />
        )}

        {/* Schedule Update Modal */}
        {showUpdateModal && (
          <ScheduleUpdateModal
            title={`Update Drivers — ${device?.hostname}`}
            subtitle={`${device?.manufacturer} ${device?.model} · The agent will download and run ${device?.manufacturer} update tools.`}
            onConfirm={async (scheduledAt) => {
              try {
                const url = scheduledAt
                  ? `${API_URL}/devices/${deviceId}/update-drivers?scheduled_at=${encodeURIComponent(scheduledAt)}`
                  : `${API_URL}/devices/${deviceId}/update-drivers`;
                await axios.post(url, null, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                });
                setShowUpdateModal(false);
                alert(scheduledAt
                  ? `✅ Update scheduled for ${new Date(scheduledAt).toLocaleString()}`
                  : "✅ Update command queued. The agent will process it on its next check-in."
                );
              } catch (err) {
                alert(err.response?.data?.detail || "❌ Failed to queue update. Please try again.");
              }
            }}
            onCancel={() => setShowUpdateModal(false)}
          />
        )}
        {/* Third-party tool disclaimer */}
        <p className="text-gray-500 text-xs mt-6">
          HP Image Assistant, Dell Command Update, and Lenovo System Updater are third-party tools owned and maintained by their respective manufacturers. AleroSys is not affiliated with HP, Dell, or Lenovo. These tools are made available for customer convenience only — AleroSys is not responsible for the content, updates, or behavior of manufacturer-released tools.
        </p>
      </main>
    </div>
  );
}